import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTutorial } from "@/contexts/TutorialContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sparkles, ChevronRight, BarChart3, CalendarDays,
  Flame, Clock, Loader2, X, SkipForward, Plus,
  ChevronDown, ChevronUp, Check, Heart, Wallet
} from "lucide-react";
import TutorialTooltip from "@/components/TutorialTooltip";
import { toLocalDateStr, formatPriceRange } from "@/lib/utils";
import { MEAL_CATEGORIES, groupMealsByCategory } from "@/lib/mealGrouping";
import { LIKED_TIPS_KEY, normalizeLikedTip, tipKeyOf } from "@/lib/likedTips";
import { isLocked } from "@/utils/subscription";
import { CrownIcon, LockOverlay } from "@/components/LockBadge";

function getDailyGreeting(t, name, lang) {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const key = `dynGreeting_${dayOfYear % 50}`;
  const greeting = t(key);
  if (greeting === key) return `${t("greeting")}, ${name}!`;
  return greeting.replace("{name}", name);
}

export default function HomePage() {
  const { user, api } = useAuth();
  const { t, lang } = useLanguage();
  const { tutorialActive, tutorialStep, updateAppState } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const locked = isLocked(user);
  const [todayMeals, setTodayMeals] = useState(null);
  const [dishCosts, setDishCosts] = useState({});   // recipe_id -> відповідь calculate-cost
  const [tips, setTips] = useState([]);
  const [likedTips, setLikedTips] = useState(() => {
    try {
      const stored = localStorage.getItem(LIKED_TIPS_KEY);
      return stored ? JSON.parse(stored).map(normalizeLikedTip) : [];
    } catch { return []; }
  });
  const [loadingMeals, setLoadingMeals] = useState(true);
  const [loadingTips, setLoadingTips] = useState(false);
  const [surpriseLoading, setSurpriseLoading] = useState(false);
  const [skipModal, setSkipModal] = useState(null);
  const [skipComment, setSkipComment] = useState("");
  const [todayCollapsed, setTodayCollapsed] = useState(
    () => localStorage.getItem("mealpill_today_collapsed") === "1"
  );

  // Update tutorial state when entering home page
  useEffect(() => {
    updateAppState({ currentPage: location.pathname });
  }, [location.pathname, updateAppState]);

  // Вартість страв дня. Інгредієнти вже лежать у страві (`meal.ingredients` у тому ж
  // форматі {name, amount, unit}, що приймає ендпойнт), тож по рецепт ходити не треба —
  // лише порахувати. Запит на страву: пакетного прийому ендпойнт не має, а страв на
  // сьогодні 3-4, тож Promise.allSettled дешевший за зайву ручку в API.
  //
  // Кожна страва падає окремо: allSettled, а не all, щоб одна помилка не з'їла ціни
  // решти. Помилка = ключа в мапі просто не буде -> рядок не рендериться.
  //
  // Оголошено ВИЩЕ за fetchTodayMeals: та тримає цю функцію у своєму масиві залежностей,
  // а він обчислюється під час рендера — на const нижче він упав би в temporal dead zone.
  const fetchDishCosts = useCallback(async (meals) => {
    const list = (meals || []).filter((m) => m?.recipe_id && (m.ingredients || []).length);
    if (!list.length) { setDishCosts({}); return; }
    const results = await Promise.allSettled(
      list.map((m) => api().post("/recipes/calculate-cost", { ingredients: m.ingredients }))
    );
    const next = {};
    results.forEach((r, i) => {
      if (r.status === "fulfilled") next[list[i].recipe_id] = r.value?.data;
      else console.error(r.reason);
    });
    setDishCosts(next);
  }, [api]);

  const fetchTodayMeals = useCallback(async () => {
    try {
      const res = await api().get(`/meal-plans/today?date=${toLocalDateStr()}&lang=${lang}`);
      setTodayMeals(res.data);
      fetchDishCosts(res.data?.meals);   // не await: меню має з'явитись, не чекаючи цін
    } catch (err) { console.error(err); }
    setLoadingMeals(false);
  }, [api, lang, fetchDishCosts]);

  const fetchTips = useCallback(async () => {
    if (isLocked(user)) return;  // не-підписник: не викликаємо гейтнутий /ai/daily-tips (уникаємо 402)
    setLoadingTips(true);
    try {
      const res = await api().get("/ai/daily-tips");
      setTips(res.data?.tips || []);
    } catch (err) { console.error(err); }
    setLoadingTips(false);
  }, [api, user]);

  useEffect(() => { fetchTodayMeals(); fetchTips(); }, [fetchTodayMeals, fetchTips]);

  // Re-fetch meals when page becomes visible (e.g. returning from another tab or route)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchTodayMeals();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    // Also re-fetch on window focus (covers navigation back via bottom nav)
    const onFocus = () => fetchTodayMeals();
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchTodayMeals]);

  const handleSurpriseMe = async () => {
    setSurpriseLoading(true);
    try {
      const res = await api().post("/ai/surprise-me", { language: lang }, { redirectOnPaywall: true });
      if (res.data?.id) navigate(`/recipe/${res.data.id}`);
    } catch (err) { console.error(err); }
    setSurpriseLoading(false);
  };

  const handleSkipMeal = async () => {
    if (!todayMeals?.plan_id || !skipModal) return;
    const dish = skipModal;
    try {
      await api().post("/meal-plans/skip-meal", {
        plan_id: todayMeals.plan_id, date: todayMeals.date,
        meal_type: dish.meal_type, recipe_id: dish.recipe_id,
        comment: skipComment || null,
      });
      await fetchTodayMeals();
    } catch (err) { console.error(err); }
    setSkipModal(null);
    setSkipComment("");
  };

  // Toggle like for a tip
  const toggleLikeTip = (tipIdx) => {
    const tip = tips[tipIdx];
    const tipKey = tipKeyOf(tip);
    const wasLiked = likedTips.some((t) => tipKeyOf(t) === tipKey);
    setLikedTips((prev) => {
      const newLiked = wasLiked
        ? prev.filter((t) => tipKeyOf(t) !== tipKey)
        : [...prev, { type: tip?.type, title: tip?.title, message: tip?.message ?? null }];
      localStorage.setItem(LIKED_TIPS_KEY, JSON.stringify(newLiked));
      return newLiked;
    });
    // Log tip_save only when transitioning to liked (fire-and-forget)
    if (!wasLiked) {
      api().post("/tips/like", { type: tip?.type, title: tip?.title, message: tip?.message }).catch(() => {});
    }
  };

  const isTipLiked = (tip) => {
    const tipKey = tipKeyOf(tip);
    return likedTips.some((t) => tipKeyOf(t) === tipKey);
  };

  // Group meals by type - ALWAYS show all categories
  const meals = todayMeals?.meals || [];
  const groupedMeals = groupMealsByCategory(meals);

  const sectionColors = {
    breakfast: { border: "border-l-[#E9C46A]", text: "text-[#8B6914]", bg: "bg-[#E9C46A]/8" },
    lunch: { border: "border-l-[#7B9E7F]", text: "text-[#4A6B4E]", bg: "bg-[#7B9E7F]/8" },
    dinner: { border: "border-l-[#D4856B]", text: "text-[#B5654B]", bg: "bg-[#D4856B]/8" },
    snack: { border: "border-l-[#A5A58D]", text: "text-[#6B705C]", bg: "bg-[#A5A58D]/8" },
  };

  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <div className="min-h-screen bg-[#FAFAF8] pb-32" data-testid="home-page">
      {/* Header with dynamic greeting */}
      <div className="pl-[72px] pr-5 pt-6 pb-4">
        <p className="font-body text-xs text-[#A5A58D] uppercase tracking-[0.2em] font-bold">Meal Pill</p>
        <h1 className="font-heading text-2xl text-[#3D4035] mt-1 leading-snug" data-testid="dynamic-greeting">
          {getDailyGreeting(t, firstName, lang)}
        </h1>
      </div>

      {/* Today's Menu - synced with planner */}
      <div className="px-5 mb-6" data-testid="todays-meals-section">
        {todayCollapsed && !tutorialActive ? (
          <button
            onClick={() => {
              setTodayCollapsed(false);
              localStorage.setItem("mealpill_today_collapsed", "0");
            }}
            className="w-full bg-white rounded-2xl px-5 py-4 text-left shadow-[0_2px_12px_rgb(0,0,0,0.04)] border border-black/5 hover:shadow-md transition-all flex items-center justify-between"
            data-testid="todays-menu-collapsed-hint"
          >
            <div>
              <h2 className="font-heading text-xl text-[#3D4035]">{t("todaysMenu")}</h2>
              <p className="font-body text-xs text-[#A5A58D] mt-0.5">{t("todaysMenuCollapsedHint")}</p>
            </div>
            <ChevronDown className="w-5 h-5 text-[#A5A58D] shrink-0" strokeWidth={2} />
          </button>
        ) : (
          <button
            onClick={() => {
              if (tutorialActive) return;
              setTodayCollapsed(v => {
                const next = !v;
                localStorage.setItem("mealpill_today_collapsed", next ? "1" : "0");
                return next;
              });
            }}
            className="w-full flex items-center justify-between mb-4"
            data-testid="todays-menu-toggle"
          >
            <h2 className="font-heading text-xl text-[#3D4035]">{t("todaysMenu")}</h2>
            {!tutorialActive && <ChevronUp className="w-5 h-5 text-[#A5A58D]" strokeWidth={2} />}
          </button>
        )}
        {(!todayCollapsed || tutorialActive) && (
          loadingMeals ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#7B9E7F] animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            {meals.length === 0 ? (
              <div className="bg-white/60 rounded-2xl border border-black/5 p-8 text-center">
                <p className="font-body text-sm text-[#A5A58D] mb-4">{t("noPlanForToday")}</p>
                <button
                  data-testid="home-go-planner"
                  onClick={() => navigate("/planner")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#7B9E7F] text-white font-body text-sm font-semibold hover:bg-[#6B8E6F] transition-colors"
                >
                  <CalendarDays className="w-4 h-4" strokeWidth={2} />
                  {t("planForDay")}
                </button>
              </div>
            ) : (
            MEAL_CATEGORIES.map((mealType) => {
              const dishes = groupedMeals[mealType] || [];
              if (dishes.length === 0) return null;
              const colors = sectionColors[mealType] || sectionColors.snack;

              return (
                <div key={mealType} data-testid={`meal-section-${mealType}`}>
                  {/* Section header */}
                  <div className="flex items-center justify-between mb-2.5 pl-1">
                    <h3 className={`font-heading text-lg ${colors.text}`}>
                      {t(mealType)}
                    </h3>
                  </div>

                  {(
                    <>
                      {/* Dish cards */}
                      <div className="space-y-2">
                        {dishes.map((dish, idx) => (
                          <div key={dish.recipe_id || idx} data-testid={`dish-card-${dish.recipe_id || idx}`}
                            className={`rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] border overflow-hidden border-l-4 ${
                              dish.status === "done" 
                                ? "bg-[#7B9E7F]/10 border-[#7B9E7F]/30 border-l-[#7B9E7F]" 
                                : `bg-white border-black/5 ${colors.border}`
                            }`}>
                            {dish.status === "skipped" ? (
                              <div className="p-4 flex items-center gap-3 opacity-40">
                                <span className="font-body text-sm text-[#A5A58D] line-through">{t("skipped")}</span>
                                {dish.skip_comment && <span className="font-body text-xs text-[#A5A58D]">— {dish.skip_comment}</span>}
                              </div>
                            ) : dish.status === "done" ? (
                          /* Done/Cooked dish - warm green, no action buttons, but clickable */
                          <button
                            data-testid={`done-card-${dish.recipe_id}`}
                            onClick={() => dish.recipe_id && navigate(`/recipe/${dish.recipe_id}?plan_id=${todayMeals?.plan_id || ""}&date=${todayMeals?.date || ""}`)}
                            className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#7B9E7F]/15 transition-colors"
                          >
                            <div className="w-10 h-10 rounded-full bg-[#7B9E7F]/20 flex items-center justify-center shrink-0">
                              <Check className="w-5 h-5 text-[#7B9E7F]" strokeWidth={2} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-body font-semibold text-[#4A6B4E] truncate">{dish.recipe_title}</p>
                              <p className="font-body text-xs text-[#7B9E7F] mt-0.5">{t("cooked")}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-[#7B9E7F] shrink-0" strokeWidth={1.5} />
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => dish.recipe_id && navigate(`/recipe/${dish.recipe_id}?plan_id=${todayMeals?.plan_id || ""}&date=${todayMeals?.date || ""}`)}                              className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#FAFAF8]/60 transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-body font-semibold text-[#3D4035] truncate">{dish.recipe_title}</p>
                                {dish.recipe_description && (
                                  <p className="font-body text-xs text-[#A5A58D] truncate mt-0.5">{dish.recipe_description}</p>
                                )}
                                {dish.dish_subtype && (
                                  <span className="font-body text-[10px] text-[#A5A58D] uppercase tracking-wider">{t(dish.dish_subtype)}</span>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                {dish.calories && (
                                  <span className="flex items-center gap-1 text-xs font-body text-[#A5A58D]">
                                    <Flame className="w-3 h-3" strokeWidth={1.5} />{dish.calories}
                                  </span>
                                )}
                                {dish.cook_time && (
                                  <span className="flex items-center gap-1 text-xs font-body text-[#A5A58D]">
                                    <Clock className="w-3 h-3" strokeWidth={1.5} />{dish.cook_time} {t("cookTimeShort")}
                                  </span>
                                )}
                              </div>
                              <ChevronRight className="w-4 h-4 text-[#A5A58D] shrink-0" strokeWidth={1.5} />
                            </button>
                            {/* Нижній ряд картки: дії зліва, вартість справа. Ціна живе тут,
                                а не в стовпчику з калоріями, бо там вона додавала висоти самій
                                клікабельній кнопці й роздувала картку. Тут вона займає порожнє
                                місце, яке в ряду й так було.
                                #7B9E7F — той самий зелений, що смужка обіду (sectionColors.lunch)
                                і головна CTA. ml-auto, а не justify-between: ряд лишається
                                flex-wrap, і при переносі ціна тиснеться вправо на своєму рядку,
                                а не розганяє кнопки по краях. */}
                            <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
                              <button data-testid={`skip-dish-${dish.recipe_id}`}
                                onClick={() => setSkipModal(dish)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EBE9E4] text-[#6B705C] font-body text-xs font-semibold hover:bg-[#E0DDD7] transition-colors">
                                <SkipForward className="w-3 h-3" strokeWidth={1.5} />
                                {t("skipMeal")}
                              </button>
                              {formatPriceRange(
                                dishCosts[dish.recipe_id]?.cost_min_total,
                                dishCosts[dish.recipe_id]?.cost_max_total
                              ) && (
                                <span className="flex items-center gap-1 ml-auto shrink-0 text-sm font-body font-semibold text-[#7B9E7F]">
                                  <Wallet className="w-3.5 h-3.5" strokeWidth={1.5} />
                                  {formatPriceRange(
                                    dishCosts[dish.recipe_id]?.cost_min_total,
                                    dishCosts[dish.recipe_id]?.cost_max_total
                                  )}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                    </>
                  )}
                </div>
              );
            }))}

            {/* Tutorial Step 9 - First dish card on home screen */}
            {tutorialActive && tutorialStep === 9 && meals.length > 0 && !meals[0]?.cooked && (
              <TutorialTooltip step={9} targetId={`dish-card-${meals[0]?.recipe_id}`} />
            )}
          </div>
          )
        )}
      </div>

      {/* Quick Actions */}
      <div className="px-5 mb-6" data-testid="quick-actions-section">
        <h2 className="font-heading text-xl text-[#3D4035] mb-4">{t("quickActions")}</h2>
        <div className="grid grid-cols-2 gap-3">
          <button data-testid="action-favorites" onClick={() => navigate("/dishes?tab=favorites")} className="bg-white rounded-2xl p-5 text-left shadow-[0_2px_12px_rgb(0,0,0,0.04)] border border-black/5 hover:shadow-md transition-all group">
            <Heart className="w-6 h-6 text-[#7B9E7F] mb-3 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
            <span className="font-body font-semibold text-[#3D4035] text-sm">{t("favorites").charAt(0).toUpperCase() + t("favorites").slice(1)}</span>
          </button>
          <button data-testid="action-insights" onClick={locked ? () => navigate("/subscription") : () => navigate("/insights")} className="relative bg-white rounded-2xl p-5 text-left shadow-[0_2px_12px_rgb(0,0,0,0.04)] border border-black/5 hover:shadow-md transition-all group">
            {locked && <CrownIcon className="absolute top-3 right-3" />}
            <BarChart3 className="w-6 h-6 text-[#7B9E7F] mb-3 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
            <span className="font-body font-semibold text-[#3D4035] text-sm">{t("smartInsights")}</span>
          </button>
          <button data-testid="action-create-recipe" onClick={() => navigate("/dishes?tab=myRecipes&create=1")} className="bg-white rounded-2xl p-5 text-left shadow-[0_2px_12px_rgb(0,0,0,0.04)] border border-black/5 hover:shadow-md transition-all group">
            <Plus className="w-6 h-6 text-[#7B9E7F] mb-3 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
            <span className="font-body font-semibold text-[#3D4035] text-sm">{t("createRecipe")}</span>
          </button>
          <button data-testid="action-surprise-me" onClick={locked ? () => navigate("/subscription") : handleSurpriseMe} disabled={surpriseLoading} className="relative bg-[#D4856B] rounded-2xl p-5 text-left shadow-[0_4px_16px_rgb(212,133,107,0.2)] hover:shadow-md transition-all group disabled:opacity-60">
            {locked && <CrownIcon className="absolute top-3 right-3" />}
            {surpriseLoading ? <Loader2 className="w-6 h-6 text-white mb-3 animate-spin" /> : <Sparkles className="w-6 h-6 text-white mb-3 group-hover:scale-110 transition-transform" strokeWidth={1.5} />}
            <span className="font-body font-semibold text-white text-sm">{t("surpriseMe")}</span>
          </button>
        </div>
      </div>

      {/* Daily Tips */}
      <div className="px-5 mb-6" data-testid="daily-tips-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl text-[#3D4035]">{t("dailyTips")}</h2>
          <button
            data-testid="view-all-tips-btn"
            onClick={locked ? () => navigate("/subscription") : () => navigate("/tips")}
            className="flex items-center gap-1 text-[#7B9E7F] font-body text-sm font-semibold hover:text-[#6B8E6F] transition-colors"
          >
            {t("viewAll") || "View All"}
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        {locked ? (
          <LockOverlay>
            <div className="flex flex-col gap-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-4 border border-black/5">
                  <div className="h-3 w-24 bg-[#E8E5E0] rounded-full mb-2" />
                  <div className="h-3 w-3/4 bg-[#EBE9E4] rounded-full mb-1.5" />
                  <div className="h-3 w-2/3 bg-[#EBE9E4] rounded-full" />
                </div>
              ))}
            </div>
          </LockOverlay>
        ) : loadingTips ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-[#7B9E7F] animate-spin" /></div>
        ) : tips.length > 0 ? (
          <div className="max-h-64 overflow-y-auto pb-2 scrollbar-hide snap-y snap-mandatory flex flex-col gap-3">
            {tips.slice(0, 3).map((tip, idx) => (
              <div key={idx} data-testid={`tip-card-${idx}`} className="shrink-0 snap-start bg-white rounded-2xl p-4 shadow-[0_2px_12px_rgb(0,0,0,0.04)] border border-black/5 relative flex flex-col">
                <button
                  data-testid={`tip-like-${idx}`}
                  onClick={() => toggleLikeTip(idx)}
                  className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    isTipLiked(tip)
                      ? "bg-[#D4856B] text-white"
                      : "bg-[#F2E9E4] text-[#A5A58D] hover:bg-[#E8E5E0]"
                  }`}
                >
                  <Heart className="w-4 h-4" strokeWidth={1.5} fill={isTipLiked(tip) ? "currentColor" : "none"} />
                </button>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-body font-bold uppercase tracking-wider inline-block mb-1 self-start ${
                  tip.type === "warning" ? "bg-[#BC4749]/10 text-[#BC4749]" : tip.type === "nutrition" ? "bg-[#7B9E7F]/10 text-[#4A6B4E]" : "bg-[#E9C46A]/10 text-[#8B6914]"
                }`}>{t(`tipType_${tip.type}`)}</span>
                <p className="font-body font-semibold text-[#3D4035] text-sm mb-0.5 pr-10">{tip.title}</p>
                <p className="font-body text-xs text-[#6B705C] leading-relaxed pr-10">{tip.message}</p>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Skip Meal Modal */}
      {skipModal && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-end" data-testid="skip-meal-modal">
          <div className="w-full max-w-[480px] mx-auto bg-[#FAFAF8] rounded-t-3xl max-h-[85vh] overflow-y-auto p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl text-[#3D4035]">{t("skipMealTitle")}</h3>
              <button onClick={() => { setSkipModal(null); setSkipComment(""); }} className="w-9 h-9 rounded-full bg-[#EBE9E4] flex items-center justify-center">
                <X className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
              </button>
            </div>
            <p className="font-body text-sm text-[#6B705C] mb-3">{skipModal.recipe_title}</p>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setSkipComment(t("ateElsewhere"))} className={`px-4 py-2 rounded-full font-body text-sm font-semibold transition-all ${skipComment === t("ateElsewhere") ? "bg-[#7B9E7F] text-white" : "bg-white text-[#6B705C] border border-[#E8E5E0]"}`}>{t("ateElsewhere")}</button>
              <button onClick={() => setSkipComment(t("notHungry"))} className={`px-4 py-2 rounded-full font-body text-sm font-semibold transition-all ${skipComment === t("notHungry") ? "bg-[#7B9E7F] text-white" : "bg-white text-[#6B705C] border border-[#E8E5E0]"}`}>{t("notHungry")}</button>
            </div>
            <input data-testid="skip-comment-input" type="text" value={skipComment} onChange={(e) => setSkipComment(e.target.value)}
              placeholder={t("skipReasonPlaceholder")} className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none mb-4" />
            <div className="flex gap-3">
              <button onClick={() => { setSkipModal(null); setSkipComment(""); }} className="flex-1 bg-[#EBE9E4] text-[#6B705C] rounded-full h-12 font-body font-bold">{t("cancel")}</button>
              <button data-testid="confirm-skip-button" onClick={handleSkipMeal} className="flex-1 bg-[#7B9E7F] text-white rounded-full h-12 font-body font-bold hover:bg-[#6B8E6F] transition-all">{t("confirmSkip")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
